import { Currency } from './boels-misc.model';
import { BoelsBillingAddress } from './boels-payment-info.model';
import { PointOfService } from './boels-point-of-service.model';
import { UserEnum } from './boels-user.model';
import { Address } from './misc.model';
import { Price, BOProduct, Promotion } from './product.model';

export interface PromotionResult {
  consumedEntries?: PromotionOrderEntryConsumed[];
  description?: string;
  promotion?: Promotion;
}
export interface PromotionOrderEntryConsumed {
  adjustedUnitPrice?: number;
  code?: string;
  orderEntryNumber?: number;
  quantity?: number;
}

export declare enum PromotionLocation {
  ActiveCart = 'CART',
  Checkout = 'CHECKOUT',
  Order = 'ORDER',
  SaveForLater = 'SAVE_FOR_LATER',
  SavedCart = 'SAVED_CART',
}

export declare enum B2BPaymentTypeEnum {
  ACCOUNT_PAYMENT = 'ACCOUNT',
  CARD_PAYMENT = 'CARD',
}

export interface Voucher {
  appliedValue?: Price;
  code?: string;
  currency?: Currency;
  description?: string;
  freeShipping?: boolean;
  name?: string;
  value?: number;
  valueFormatted?: string;
  valueString?: string;
  voucherCode?: string;
}

export interface DeliveryModeResponse {
  deliveryModes: DeliveryMode[];
}

export enum DeliveryModeType {
  PICKUP = 'pickup-mode',
  DELIVERY = 'delivery-mode',
}

export interface DeliveryMode {
  code?: DeliveryModeType;
  deliveryCost?: Price;
  description?: string;
  name?: string;
}

export interface OrderEntry {
  accessoryType?: string;
  orderCode?: string;
  basePrice?: Price;
  deliveryMode?: DeliveryMode;
  deliveryPointOfService?: PointOfService;
  entryNumber?: number;
  product?: BOProduct;
  quantity?: number;
  totalPrice?: Price;
  updateable?: boolean;
  returnedItemsPrice?: Price;
  returnableQuantity?: number;
  cancelledItemsPrice?: Price;
  cancellableQuantity?: number;
  promotions?: PromotionResult[];
  isAccessory?: boolean;
  isMandatory?: boolean;
  minQuantity?: number;
  requiredQuantity?: number;
}

export interface OrderEntryGroup extends OrderEntry {
  accessories?: OrderEntry[];
}

export interface DeliveryOrderEntryGroup {
  deliveryAddress?: Address;
  entries?: OrderEntry[];
  quantity?: number;
  totalPriceWithTax?: Price;
}

export interface PickupOrderEntryGroup {
  deliveryPointOfService?: PointOfService;
  distance?: number;
  entries?: OrderEntry[];
  quantity?: number;
  totalPriceWithTax?: Price;
}

export interface Principal {
  name?: string;
  uid?: string;
}

export interface CardType {
  code?: string;
  name?: string;
}

export interface PaymentType {
  code?: string;
  displayName?: string;
}

export interface CostCenter {
  active?: boolean;
  code?: string;
  name?: string;
  unit?: B2BUnit;
}

export interface B2BUnit {
  active?: boolean;
  addresses?: Address[];
  uid?: string;
  name?: string;
}

export interface PaymentDetails {
  accountHolderName?: string;
  billingAddress?: BoelsBillingAddress;
  projectReference?: string;
  cardNumber?: string;
  cardType?: CardType;
  cvn?: string;
  defaultPayment?: boolean;
  expiryMonth?: string;
  expiryYear?: string;
  id?: string;
  issueNumber?: string;
  saved?: boolean;
  startMonth?: string;
  startYear?: string;
  subscriptionId?: string;
}

export interface SaveCartResult {
  savedCartData?: Cart;
}

export interface Cart {
  appliedOrderPromotions?: PromotionResult[];
  appliedProductPromotions?: PromotionResult[];
  appliedVouchers?: Voucher[];
  calculated?: boolean;
  code?: string;
  costCenter?: CostCenter;
  deliveryAddress?: Address;
  deliveryCost?: Price;
  deliveryItemsQuantity?: number;
  deliveryMode?: DeliveryMode;
  deliveryOrderGroups?: DeliveryOrderEntryGroup[];
  description?: string;
  entries?: OrderEntryGroup[];
  expirationTime?: Date;
  guid: string;
  name?: string;
  net?: boolean;
  orderDiscounts?: Price;
  paymentInfo?: PaymentDetails;
  paymentType?: PaymentType;
  pickupItemsQuantity?: number;
  pickupOrderGroups?: PickupOrderEntryGroup[];
  potentialOrderPromotions?: PromotionResult[];
  potentialProductPromotions?: PromotionResult[];
  productDiscounts?: Price;
  purchaseOrderNumber?: string;
  saveTime?: Date;
  savedBy?: Principal;
  site?: string;
  store?: string;
  subTotal?: Price;
  totalDiscounts?: Price;
  totalItems?: number;
  totalPrice?: Price;
  totalPriceWithTax?: Price;
  totalTax?: Price;
  totalUnitCount?: number;
  totalMainUnitCount?: number;
  user?: Principal;
  startDate?: string;
  endDate?: string;
  remarks?: string;
  cutOffTimestamp?: string;
  operationalDirections?: string;
}

export interface EntryGroup {
  accessories?: {
    product: {
      code: string;
    };
    quantity: number;
  }[];
  product: {
    code: string;
  };
  quantity: number;
}

export interface EntryGroupUpdate {
  entryNumber: number;
  quantity: number;
  accessories?: EntryGroupUpdateAccessory[];
}

export type EntryGroupUpdateAccessory = {
  product?: BOProduct;
  entryNumber: number;
  quantity: number;
  isAccessory?: boolean;
};

export interface OrderAccessoryEntry {
  productCode: string;
  quantity: number;
  mainProduct: string;
}

export interface GroupOrderModification
  extends CartModification,
    CartModificationList {
  accessories?: CartModificationList;
}

export interface CartModification {
  entry?: OrderEntry;
  deliveryModeChanged?: boolean;
  statusCode?: string;
  statusMessage?: string;
  quantity?: number;
  quantityAdded?: number;
}

interface CartModificationList {
  cartModifications?: CartModification[];
}

export declare enum CartValidationStatusCode {
  NO_STOCK = 'noStock',
  LOW_STOCK = 'lowStock',
  REVIEW_CONFIGURATION = 'reviewConfiguration',
  PRICING_ERROR = 'pricingError',
  UNRESOLVABLE_ISSUES = 'unresolvableIssues',
}

export type CartUser = {
  type: number;
  authToken?: string;
  b2cToken?: string;
};

export enum CartUrlEnum {
  CREATE,
  CARTS,
  ORDERS,
  ADDRESS,
}

export enum CartUrlValueEnum {
  ANONYMOUS_CART = 'users/anonymous/carts',
  ANONYMOUS_ORDER = 'users/anonymous/orders',
  ANONYMOUS_ADRESS = 'users/anonymous/addresses/verification',
  REGISTERED_CART = 'users/current/carts',
  REGISTERED_CART_CURRENT = 'users/current/carts/current',
  REGISTERED_ORDER = 'users/current/orders',
  REGISTERED_ADRESS = 'users/current/addresses/verification',
}

export type LocalCartDataType = {
  user_type: UserEnum;
  cartId: string;
};

export interface Order {
  appliedOrderPromotions?: PromotionResult[];
  appliedProductPromotions?: PromotionResult[];
  appliedVouchers?: Voucher[];
  calculated?: boolean;
  code?: string;
  costCenter?: CostCenter;
  created?: Date;
  deliveryAddress?: Address;
  deliveryCost?: Price;
  deliveryItemsQuantity?: number;
  deliveryMode?: DeliveryMode;
  deliveryOrderGroups?: DeliveryOrderEntryGroup[];
  deliveryStatus?: string;
  deliveryStatusDisplay?: string;
  entries?: OrderEntryGroup[];
  guestCustomer?: boolean;
  guid?: string;
  net?: boolean;
  orderDiscounts?: Price;
  paymentInfo?: PaymentDetails;
  pickupItemsQuantity?: number;
  pickupOrderGroups?: PickupOrderEntryGroup[];
  productDiscounts?: Price;
  purchaseOrderNumber?: string;
  site?: string;
  status?: string;
  statusDisplay?: string;
  store?: string;
  subTotal?: Price;
  totalDiscounts?: Price;
  totalItems?: number;
  totalPrice?: Price;
  totalPriceWithTax?: Price;
  totalTax?: Price;
  unconsignedEntries?: OrderEntry[];
  user?: Principal;
  returnable?: boolean;
  cancellable?: boolean;
  startDate?: string;
  endDate?: string;
  useRentalWall?: boolean;
  remarks?: string;
  cutOffTimestamp?: string;
  newsletterConsent?: boolean;
  newsletterConsentType?: string;
  operationalDirections?: string;
}
